import React, { useMemo, useState } from 'react';
import { X, Download, Upload, AlertTriangle, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  calculateServiceYears,
  normalizeServiceHistoryEntry,
  sortServiceHistory
} from '../utils/serviceHistoryUtils';

export const SERVICE_HISTORY_HEADERS = [
  'Company',
  'Member ID',
  'DOJ',
  'DOL',
  'PF/EPF',
  'EPS',
  'Form 31',
  'Form 19',
  'Form 10C',
  'Form-13'
];

const HEADER_ALIASES = {
  company: 'company_name',
  'company name': 'company_name',
  company_name: 'company_name',
  'member id': 'member_id',
  member_id: 'member_id',
  memberid: 'member_id',
  doj: 'doj',
  'date of joining': 'doj',
  dol: 'dol',
  'date of leaving': 'dol',
  'pf/epf': 'pf_epf_balance',
  pf_epf: 'pf_epf_balance',
  'pf epf': 'pf_epf_balance',
  pf: 'pf_epf_balance',
  epf: 'pf_epf_balance',
  eps: 'eps_balance',
  'eps balance': 'eps_balance',
  'form 31': 'form_31',
  form_31: 'form_31',
  'form 19': 'form_19',
  form_19: 'form_19',
  'form 10c': 'form_10c_scheme_certificate',
  form_10c: 'form_10c_scheme_certificate',
  'scheme certificate': 'form_10c_scheme_certificate',
  'form-13': 'form_13',
  'form 13': 'form_13',
  form_13: 'form_13',
  form13: 'form_13'
};

const yesNo = (value) => {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return 'No';
  if (['y', 'yes', 'true', '1'].includes(v)) return 'Yes';
  if (['n', 'no', 'false', '0'].includes(v)) return 'No';
  return String(value).trim();
};

const yesNoNa = (value) => {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return 'NA';
  if (['y', 'yes', 'true', '1'].includes(v)) return 'Yes';
  if (['n', 'no', 'false', '0'].includes(v)) return 'No';
  if (['na', 'n/a', 'not applicable', '-'].includes(v)) return 'NA';
  return 'NA';
};

const parseExcelDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && !Number.isNaN(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const mm = String(parsed.m).padStart(2, '0');
      const dd = String(parsed.d).padStart(2, '0');
      return `${parsed.y}-${mm}-${dd}`;
    }
  }
  const raw = String(value).trim();
  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = raw.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }
  // YYYY-MM-DD
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    return dt.toISOString().slice(0, 10);
  }
  return raw;
};

const mapRow = (raw) => {
  const row = {
    company_name: '',
    member_id: '',
    doj: '',
    dol: '',
    service: '',
    pf_epf_balance: '',
    eps_balance: '',
    form_31: '',
    form_19: 'No',
    form_10c_scheme_certificate: 'No',
    form_13: 'NA'
  };
  Object.entries(raw || {}).forEach(([key, value]) => {
    const mapped = HEADER_ALIASES[String(key).trim().toLowerCase()];
    if (!mapped) return;
    if (mapped === 'doj' || mapped === 'dol') {
      row[mapped] = parseExcelDate(value);
    } else if (mapped === 'form_19' || mapped === 'form_10c_scheme_certificate') {
      row[mapped] = yesNo(value);
    } else if (mapped === 'form_13') {
      row[mapped] = yesNoNa(value);
    } else if (mapped === 'pf_epf_balance' || mapped === 'eps_balance') {
      const n = String(value ?? '').replace(/,/g, '').trim();
      row[mapped] = n === '' ? '' : n;
    } else {
      row[mapped] = value == null ? '' : String(value).trim();
    }
  });
  return normalizeServiceHistoryEntry(row);
};

const validateRows = (rows) => {
  const errors = [];
  const warnings = [];
  rows.forEach((row, idx) => {
    const line = idx + 1;
    if (!row.company_name) {
      errors.push(`Row ${line}: Company is required.`);
    }
    if (row.doj && Number.isNaN(new Date(row.doj).getTime())) {
      errors.push(`Row ${line}: DOJ is not a valid date (use YYYY-MM-DD or DD/MM/YYYY).`);
    }
    if (row.dol && Number.isNaN(new Date(row.dol).getTime())) {
      errors.push(`Row ${line}: DOL is not a valid date (use YYYY-MM-DD or DD/MM/YYYY).`);
    }
    if (row.doj && row.dol && new Date(row.dol) < new Date(row.doj)) {
      errors.push(`Row ${line}: DOL is before DOJ.`);
    }
    if (!row.doj || !row.dol) {
      warnings.push(`Row ${line}: DOJ/DOL missing — Service period will be blank.`);
    }
    if (row.pf_epf_balance === '' && row.eps_balance === '') {
      warnings.push(`Row ${line}: PF/EPF and EPS balances are empty.`);
    }
    if (!['Yes', 'No'].includes(row.form_19)) {
      warnings.push(`Row ${line}: Form 19 should be Yes/No (found "${row.form_19}"). Will default to No if ignored.`);
    }
    if (!['Yes', 'No'].includes(row.form_10c_scheme_certificate)) {
      warnings.push(`Row ${line}: Form 10C should be Yes/No (found "${row.form_10c_scheme_certificate}"). Will default to No if ignored.`);
    }
  });
  if (rows.length === 0) {
    errors.push('No data rows found. Add at least one row under the header.');
  }
  return { errors, warnings };
};

const downloadTemplate = () => {
  const sample = [
    SERVICE_HISTORY_HEADERS,
    ['ABC Pvt Ltd', 'MH/12345/0001', '2018-04-01', '2022-03-31', '125000', '45000', '', 'Yes', 'No', 'No'],
    ['XYZ Ltd', 'KA/99887/0012', '01/04/2022', '15/08/2024', '80000', '22000', '1', 'No', 'Yes', 'Yes']
  ];
  const ws = XLSX.utils.aoa_to_sheet(sample);
  ws['!cols'] = SERVICE_HISTORY_HEADERS.map(() => ({ wch: 16 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Service History');
  XLSX.writeFile(wb, 'PF_Service_History_Template.xlsx');
};

/**
 * Import service history from Excel/CSV with template + validations.
 * onImport(rows, { mode: 'replace' | 'append' })
 */
const ServiceHistoryImportModal = ({ onClose, onImport }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [mode, setMode] = useState('append');
  const [forceImport, setForceImport] = useState(false);

  const { errors, warnings } = useMemo(() => validateRows(rows), [rows]);
  const canImport = rows.length > 0 && (errors.length === 0 || forceImport);

  const ingestSheet = (workbook) => {
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });
    const mapped = json.map(mapRow).filter((r) =>
      r.company_name || r.member_id || r.doj || r.dol || r.pf_epf_balance || r.eps_balance
    );
    setRows(mapped);
    setForceImport(false);
    if (!mapped.length) {
      toast.error('No usable rows found in the file');
    } else {
      toast.success(`${mapped.length} row(s) loaded for review`);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      ingestSheet(workbook);
    } catch (error) {
      console.error(error);
      toast.error('Unable to read file. Use the Excel/CSV template.');
    }
  };

  const handleImport = () => {
    if (!canImport) return;
    const cleaned = sortServiceHistory(
      rows.map((r) =>
        normalizeServiceHistoryEntry({
          ...r,
          form_19: ['Yes', 'No'].includes(r.form_19) ? r.form_19 : 'No',
          form_10c_scheme_certificate: ['Yes', 'No'].includes(r.form_10c_scheme_certificate)
            ? r.form_10c_scheme_certificate
            : 'No',
          form_13: ['Yes', 'No', 'NA'].includes(r.form_13) ? r.form_13 : 'NA',
          service: calculateServiceYears(r.doj, r.dol)
        })
      )
    );
    onImport(cleaned, { mode });
    toast.success(`${cleaned.length} service history row(s) imported`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-primary-600" />
              Import Service History
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Use the template format, review validations, then import (warnings can be ignored).
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="rounded-lg border border-primary-100 bg-primary-50/60 p-4">
            <div className="text-sm font-semibold text-slate-900 mb-2">Required template columns</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border border-slate-200 bg-white rounded-md overflow-hidden">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    {SERVICE_HISTORY_HEADERS.map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left font-semibold border-b border-slate-200 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-slate-700">
                    <td className="px-2 py-1.5 border-b border-slate-100">ABC Pvt Ltd</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">MH/12345/0001</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">2018-04-01</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">2022-03-31</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">125000</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">45000</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">(optional)</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">Yes / No</td>
                    <td className="px-2 py-1.5 border-b border-slate-100">Yes / No</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul className="mt-3 text-xs text-slate-600 space-y-1 list-disc list-inside">
              <li>First row must be the header names exactly as shown (aliases like “Company Name” also work).</li>
              <li>Dates: <code>YYYY-MM-DD</code> or <code>DD/MM/YYYY</code>. Service period is calculated from DOJ–DOL.</li>
              <li>Form 19 / Form 10C accept Yes/No (Y/N also OK).</li>
              <li>Form-13 accept Yes / No / NA (No = transfer still needed).</li>
            </ul>
            <button type="button" onClick={downloadTemplate} className="btn-secondary inline-flex items-center text-sm mt-3">
              <Download className="w-4 h-4 mr-1.5" />
              Download Excel template
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="btn-primary inline-flex items-center text-sm cursor-pointer">
              <Upload className="w-4 h-4 mr-1.5" />
              Choose Excel / CSV
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
            {fileName && <span className="text-xs text-slate-500 truncate max-w-[220px]">{fileName}</span>}
            <div className="flex items-center gap-3 text-sm ml-auto">
              <label className="inline-flex items-center gap-1.5">
                <input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} />
                Append
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
                Replace existing
              </label>
            </div>
          </div>

          {rows.length > 0 && (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 text-xs font-medium text-slate-600 flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                Preview · {rows.length} row(s)
              </div>
              <div className="max-h-40 overflow-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="px-2 py-1">#</th>
                      <th className="px-2 py-1">Company</th>
                      <th className="px-2 py-1">Member ID</th>
                      <th className="px-2 py-1">DOJ</th>
                      <th className="px-2 py-1">DOL</th>
                      <th className="px-2 py-1">PF/EPF</th>
                      <th className="px-2 py-1">EPS</th>
                      <th className="px-2 py-1">Form-13</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">{r.company_name || '—'}</td>
                        <td className="px-2 py-1">{r.member_id || '—'}</td>
                        <td className="px-2 py-1">{r.doj || '—'}</td>
                        <td className="px-2 py-1">{r.dol || '—'}</td>
                        <td className="px-2 py-1">{r.pf_epf_balance || '—'}</td>
                        <td className="px-2 py-1">{r.eps_balance || '—'}</td>
                        <td className="px-2 py-1">{r.form_13 || 'NA'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(errors.length > 0 || warnings.length > 0) && (
            <div className="space-y-2">
              {errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <div className="font-semibold mb-1">Errors ({errors.length})</div>
                  <ul className="list-disc list-inside text-xs space-y-0.5 max-h-28 overflow-auto">
                    {errors.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              )}
              {warnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="font-semibold mb-1 inline-flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Warnings ({warnings.length})
                  </div>
                  <ul className="list-disc list-inside text-xs space-y-0.5 max-h-28 overflow-auto">
                    {warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                </div>
              )}
              {errors.length > 0 && (
                <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={forceImport}
                    onChange={(e) => setForceImport(e.target.checked)}
                  />
                  <span>
                    Ignore errors/warnings and import anyway. Invalid Form Yes/No values will default to No; incomplete rows are still imported.
                  </span>
                </label>
              )}
              {errors.length === 0 && warnings.length > 0 && (
                <p className="text-xs text-slate-500">Warnings will not block import. Click Import to continue.</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            type="button"
            disabled={!canImport}
            onClick={handleImport}
            className="btn-primary inline-flex items-center disabled:opacity-50"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            Import {rows.length ? `(${rows.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceHistoryImportModal;
